import { ArrowPathIcon } from '@heroicons/react/24/outline';
import React, { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useGetDevTestScenariosQuery,
  useRouteDevCallMutation,
} from '@/services/api/blandService';

interface DevCallRouterProps {
  workflowId: string;
  phoneNumber: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRouted?: () => void;
}

export const DevCallRouter: React.FC<DevCallRouterProps> = ({
  workflowId,
  phoneNumber,
  open,
  onOpenChange,
  onRouted,
}) => {
  const [selectedScenario, setSelectedScenario] = useState<string>('DEFAULT');

  const { data: scenarios, isLoading: loadingScenarios } =
    useGetDevTestScenariosQuery();
  const [routeCall, { isLoading: routing }] = useRouteDevCallMutation();

  const handleRoute = async () => {
    if (!selectedScenario) {
      toast.error('Please select a test scenario');
      return;
    }

    try {
      const result = await routeCall({
        workflowId,
        testScenario: selectedScenario,
      }).unwrap();

      toast.success(
        `Call routed to ${result.description} (${result.routed_to})`,
      );

      onOpenChange(false);
      onRouted?.();
    } catch (error) {
      toast.error('Failed to route call');
      // Log error for debugging
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.error('Route error:', error);
      }
    }
  };

  if (!scenarios?.is_dev_environment) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Route Test Call</DialogTitle>
          <DialogDescription>
            Select where to route the call to {phoneNumber}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4 flex-1 overflow-y-auto">
          <div className="space-y-2">
            <div className="text-sm font-medium">Test Scenario</div>
            <Select
              value={selectedScenario}
              onValueChange={setSelectedScenario}
              disabled={loadingScenarios || routing}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a test scenario" />
              </SelectTrigger>
              <SelectContent>
                {scenarios?.scenarios.map((scenario) => (
                  <SelectItem key={scenario.name} value={scenario.name}>
                    <div>
                      <div className="font-medium">{scenario.description}</div>
                      <div className="text-xs text-gray-500">
                        {scenario.number} - {scenario.behavior}
                      </div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="rounded-lg bg-amber-50 p-3">
            <p className="text-sm text-amber-800">
              <strong>Original number:</strong> {phoneNumber}
            </p>
            {selectedScenario && scenarios?.scenarios && (
              <p className="text-sm text-amber-800 mt-1">
                <strong>Will route to:</strong>{' '}
                {
                  scenarios.scenarios.find((s) => s.name === selectedScenario)
                    ?.number
                }
              </p>
            )}
          </div>
        </div>

        <DialogFooter className="flex-shrink-0">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={routing}
          >
            Cancel
          </Button>
          <Button onClick={handleRoute} disabled={!selectedScenario || routing}>
            {routing && <ArrowPathIcon className="mr-2 h-4 w-4 animate-spin" />}
            Route Call
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
